// Roguelike challenge system — gates story sentence unlocks behind visible goals.

export interface Challenge {
  id: number
  name: string
  description: string
  predicate: (s: ChallengeState) => boolean
  /** Current progress as a fraction 0–1 (for progress bar) */
  progress: (s: ChallengeState) => number
  reward: {
    sentenceIndex: number
    toast?: string
  }
  /** Challenge ID that must complete before this one shows */
  revealAfter?: number
}

export interface ChallengeState {
  letters: { id: number; level: number }[]
  heat: number
  weight: number
  totalLength: number
  lexiconLen: number
  distinct: number
  edition: number
}

// Each challenge maps 1:1 to a STORY[] sentence index.
export const CHALLENGES: Challenge[] = [
  {
    id: 0,
    name: 'First Impression',
    description: 'Craft 5 words',
    predicate: (s) => s.lexiconLen >= 5,
    progress: (s) => Math.min(1, s.lexiconLen / 5),
    reward: { sentenceIndex: 0, toast: 'The first letter stirs...' },
  },
  {
    id: 1,
    name: 'Ink Condenser',
    description: 'Reach Fresh Ink ≥ 20',
    predicate: (s) => s.heat >= 20,
    progress: (s) => Math.min(1, (s.heat + 100) / 120),
    reward: { sentenceIndex: 1, toast: 'Fresh ink pools on the type...' },
  },
  {
    id: 2,
    name: 'Heavy Impression',
    description: 'Reach Roller Pressure ≥ 30',
    predicate: (s) => s.weight >= 30,
    progress: (s) => Math.min(1, (s.weight + 100) / 130),
    reward: { sentenceIndex: 2, toast: 'The roller presses down...' },
  },
  {
    id: 3,
    name: 'Letter Collector',
    description: 'Own 6 distinct letters',
    predicate: (s) => s.distinct >= 6,
    progress: (s) => Math.min(1, s.distinct / 6),
    reward: { sentenceIndex: 3, toast: 'Words stack upon words...' },
  },
  {
    id: 4,
    name: 'Deep Press',
    description: 'Reach Fresh Ink ≥ 60',
    predicate: (s) => s.heat >= 60,
    progress: (s) => Math.min(1, s.heat / 60),
    reward: { sentenceIndex: 4, toast: 'The press runs deep...' },
    revealAfter: 1,
  },
  {
    id: 5,
    name: 'Heavy Type',
    description: 'Reach Roller Pressure ≥ 60',
    predicate: (s) => s.weight >= 60,
    progress: (s) => Math.min(1, s.weight / 60),
    reward: { sentenceIndex: 5, toast: 'Balance shifts...' },
    revealAfter: 2,
  },
  {
    id: 6,
    name: 'Sentence Builder',
    description: 'Accumulate 80 length',
    predicate: (s) => s.totalLength >= 80,
    progress: (s) => Math.min(1, s.totalLength / 80),
    reward: { sentenceIndex: 6, toast: 'A sentence takes shape...' },
    revealAfter: 3,
  },
  {
    id: 7,
    name: 'Saturator',
    description: 'Reach Fresh Ink ≥ 80',
    predicate: (s) => s.heat >= 80,
    progress: (s) => Math.min(1, s.heat / 80),
    reward: { sentenceIndex: 7, toast: 'The ink begins to dry...' },
    revealAfter: 4,
  },
  {
    id: 8,
    name: 'Every Mark Matters',
    description: 'Own 10 distinct letters',
    predicate: (s) => s.distinct >= 10,
    progress: (s) => Math.min(1, s.distinct / 10),
    reward: { sentenceIndex: 8, toast: 'Every impression matters...' },
    revealAfter: 5,
  },
  {
    id: 9,
    name: 'Alphabet Keeper',
    description: 'Accumulate 120 length',
    predicate: (s) => s.totalLength >= 120,
    progress: (s) => Math.min(1, s.totalLength / 120),
    reward: { sentenceIndex: 9, toast: 'The alphabet breathes...' },
    revealAfter: 6,
  },
]
